import React, { JSX, useEffect, useMemo, useState } from 'react';
import HeadingWithDetails from '../../../components/HeadingWithDetails';
import { useBranding } from 'src/branding/BrandingContextProvider';
import Breadcrumb from '../../../components/Breadcrumb';
import { PATHS, USER_ROLES } from 'src/common/constants';
import { isRole } from 'src/helpers/permissions';
import { Link } from 'react-router-dom';
import { convertTime } from 'src/helpers/time';
import Card from '../../../components/Card';
import Tenant from '../../../components/Tenant';
import Avatar from '../../../components/Avatar';
import FontAwesomeIcon from '../../../components/Icon';
import Button from '../../../components/Button';
import { getAllowedTabList } from 'src/services/client';
import ServiceRequestToast from 'src/pages/ServiceRequest/ServiceRequestToast';
import { useServiceRequestDataFromContext } from 'src/pages/ServiceRequest/Context/ServiceRequestContext';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';

const PropertyLink = ({ serviceRequest, user }) => {
  const propertyViewCheck: boolean = isRole(
    [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_DEVELOPER],
    user
  );

  if (!propertyViewCheck) {
    return (
      <span className="serviceRequestDetails__links">
        {serviceRequest.suite.property.name}
      </span>
    );
  }

  const linkTo: any = useMemo(
    () => ({
      pathname: PATHS.PROPERTIES_VIEW.replace(
        ':id',
        serviceRequest.suite.property.id
      ),
      state: {
        data: {
          id: serviceRequest.suite.property.id,
          serviceRequestId: serviceRequest.id,
        },
      },
    }),
    [serviceRequest.suite.property.id, serviceRequest.id]
  );

  return (
    <Link
      to={linkTo}
      className="serviceRequestDetails__links"
      style={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {serviceRequest.suite.property.name}
    </Link>
  );
};

const SuiteLink = ({ serviceRequest, user }) => {
  const propertyViewCheck: boolean = isRole(
    [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_DEVELOPER],
    user
  );

  const linkTo: any = useMemo(
    () => ({
      pathname: PATHS.SUITES_VIEW.replace(
        ':id',
        `${serviceRequest.id}&${serviceRequest.suite.id}&${serviceRequest.suite.property.id}`
      ),
      state: {
        data: {
          id: serviceRequest.suite.id,
          serviceRequestId: serviceRequest.id,
        },
        suite: { id: serviceRequest.suite.id },
        property: { id: serviceRequest.suite.property.id },
        from: isRole(USER_ROLES.HOME_OWNER, user) ? 'Dashboard' : null,
      },
    }),
    [
      serviceRequest.id,
      serviceRequest.suite.id,
      serviceRequest.suite.property.id,
      user,
    ]
  );

  if (!propertyViewCheck) {
    return (
      <span className="serviceRequestDetails__links">
        {serviceRequest.suite.number}
      </span>
    );
  }

  return (
    <Link
      to={linkTo}
      className="serviceRequestDetails__links"
      style={{ textDecoration: 'underline', cursor: 'pointer' }}
    >
      {serviceRequest.suite.number}
    </Link>
  );
};

const AssetName = ({ serviceRequest }) => {
  return (
    <span
      className="serviceRequestDetails__links"
      style={{ textDecoration: 'none' }}
    >
      {serviceRequest.asset.name}
    </span>
  );
};

const BreadcrumbSeparator = () => {
  return <span> / </span>;
};

const WarrantyStartDate = ({ serviceRequest }) => {
  return (
    <div style={{ padding: 10, paddingLeft: 0 }}>
      Warranty start:{' '}
      <span style={{ color: 'black' }}>
        {convertTime(serviceRequest.asset.warrantyStartDate, 'date')}
      </span>
    </div>
  );
};

const WarrantyAddress = ({ serviceRequest }) => {
  if (!serviceRequest.suite.warrantyAddress) {
    return null;
  }

  return (
    <div style={{ paddingTop: 0, paddingLeft: 0 }}>
      Warranty/Civic Address:
      <span style={{ color: 'black' }}>
        {' '}
        {serviceRequest.suite.warrantyAddress}
      </span>
    </div>
  );
};

// Main Location Breadcrumb Component.
const ServiceRequestLocationBreadcrumb = ({ serviceRequest, user }) => {
  return (
    <>
      <div className="serviceRequestDetails__breadcrumb">
        <PropertyLink serviceRequest={serviceRequest} user={user} />
        <BreadcrumbSeparator />
        <SuiteLink serviceRequest={serviceRequest} user={user} />
        <BreadcrumbSeparator />
        <AssetName serviceRequest={serviceRequest} />
      </div>
      <WarrantyStartDate serviceRequest={serviceRequest} />
      <WarrantyAddress serviceRequest={serviceRequest} />
    </>
  );
};

const ServiceRequestReporterCard = ({ serviceRequest, showLink }) => {
  const createReporterHeadline = () => {
    if (!showLink) {
      return serviceRequest.reporter.name;
    }

    return (
      <a
        href={PATHS.CONTACTS_VIEW.replace(
          ':id',
          `${serviceRequest.reporter.id}&${serviceRequest.id}`
        )}
      >
        {serviceRequest.reporter.name}
      </a>
    );
  };

  return (
    <Card
      title="Reporter"
      body={
        <Tenant
          headline={createReporterHeadline()}
          email={serviceRequest.reporter.email}
          phone={serviceRequest.reporter.phone}
          strapline={serviceRequest.reporter.role}
          avatar={
            <Avatar
              url={serviceRequest.reporter.profileImage}
              badge={undefined}
              size={undefined}
            />
          }
        />
      }
    />
  );
};

const ServiceRequestHomeOwnerCard = ({ serviceRequest }) => {
  return (
    <Card
      title="Primary Home Owner"
      body={
        <Tenant
          headline={serviceRequest.suite.homeOwner.name}
          email={serviceRequest.suite.homeOwner.email}
          phone={serviceRequest.suite.homeOwner.phone}
          strapline={serviceRequest.suite.homeOwner.role}
          avatar={
            <Avatar
              url={serviceRequest.suite.homeOwner.profileImage}
              badge={undefined}
              size={undefined}
            />
          }
        />
      }
    />
  );
};

const ServiceRequestAssignedToCard = ({
  serviceRequest,
  showLink,
  navigateTo,
}) => {
  const createServiceProviderHeadline = () => {
    if (!showLink || !serviceRequest.serviceProvider.name) {
      return serviceRequest.serviceProvider.name;
    }

    return (
      <Link
        to={{
          pathname: PATHS.CONTACTS_VIEW.replace(
            ':id',
            `${serviceRequest.serviceProvider.id}&${serviceRequest.id}`
          ),
          state: { navigateTo },
        }}
      >
        {serviceRequest.serviceProvider.name}
      </Link>
    );
  };

  const renderServiceProviderContent = () => {
    if (!serviceRequest.serviceProvider.name) {
      return <div>No service provider assigned</div>;
    }

    return (
      <Tenant
        headline={createServiceProviderHeadline()}
        strapline={serviceRequest.serviceProvider.role}
        avatar={
          <Avatar
            url={serviceRequest.serviceProvider.profileImage}
            badge="service-provider"
            size={undefined}
          />
        }
      />
    );
  };

  return <Card title="Assigned To" body={renderServiceProviderContent()} />;
};

const ServiceRequestAppointmentCard = ({ serviceRequest }) => {
  const isEmptyAppointment =
    serviceRequest.appointment.id === '00000000-0000-0000-0000-000000000000';

  const formatAppointmentDate = () => {
    if (isEmptyAppointment) return 'No Appointment';
    return convertTime(
      serviceRequest.appointment.scheduledAt,
      'dddd MM/DD/YYYY'
    );
  };

  const formatAppointmentTime = () => {
    if (isEmptyAppointment) return '';
    return convertTime(serviceRequest.appointment.scheduledAt, 'h:mm A');
  };

  return (
    <Card
      title="Appointment"
      body={
        <div className="serviceRequest__appointment">
          <div className="serviceRequest__appointmentIcon">
            <FontAwesomeIcon glyph="calendar" weight="regular" />
          </div>
          <div>
            {formatAppointmentDate()}
            <br />
            {formatAppointmentTime()}
          </div>
        </div>
      }
    />
  );
};

const ServiceRequestActions = ({
  serviceRequest,
  onEditServiceRequest,
  onDeleteServiceRequest,
  onDownloadPdf,
  user,
}) => {
  const canPerformActions = isRole(
    [USER_ROLES.PROPERTY_DEVELOPER, USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN],
    user
  );

  if (!canPerformActions) return null;

  const handleDelete = () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${serviceRequest.ticketId}?`
    );
    if (confirmed) {
      onDeleteServiceRequest();
    }
  };

  return (
    <>
      <Button
        color="semantic"
        onClick={onEditServiceRequest}
        label={<FontAwesomeIcon glyph="pencil" weight="light" />}
      />
      <Button
        color="semantic"
        onClick={handleDelete}
        label={<FontAwesomeIcon glyph="trash" weight="light" />}
      />
      <Button
        color="semantic"
        onClick={onDownloadPdf}
        label={<FontAwesomeIcon glyph="download" weight="light" />}
      />
    </>
  );
};

const ServiceRequestBreadcrumb = ({ user, location, branding }) => {
  if (isRole(USER_ROLES.HOME_OWNER, user)) {
    return <Breadcrumb to={PATHS.DASHBOARD} label="Dashboard" />;
  }

  return (
    <Breadcrumb
      to={location?.state?.navigateTo ?? PATHS.SERVICE_REQUESTS_LIST}
      label={branding.text.serviceRequest + 's'}
    />
  );
};

const ServiceRequestStatusToast = ({
  serviceRequest,
  handleStatusChange,
  user,
}) => {
  const shouldShowToast: boolean = !isRole(
    [USER_ROLES.HOME_OWNER, USER_ROLES.REALTOR, USER_ROLES.OCCUPANT],
    user
  );

  if (!shouldShowToast) {
    return null;
  }

  return (
    <ServiceRequestToast
      serviceRequest={serviceRequest}
      handleStatusChange={handleStatusChange}
    />
  );
};

export const ServiceRequestDetailHeader = ({
  location,
  onEditServiceRequest,
  onDeleteServiceRequest,
  onDownloadPdf,
  navigateTo,
  handleStatusChange,
}) => {
  const { branding } = useBranding();
  const user = useSelector((state: any) => selectAuthenticatedUser(state));
  const [homeOwnerDataVisibility, setHomeOwnerDataVisibility] = useState(false);
  const { serviceRequest } = useServiceRequestDataFromContext();

  const showLink: boolean = isRole(
    [
      USER_ROLES.ADMIN,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PROPERTY_DEVELOPER,
      USER_ROLES.STRATA_MANAGER,
    ],
    user
  );

  // Fetch homeowner data visibility.
  useEffect(() => {
    let active: boolean = true;

    getAllowedTabList(serviceRequest.propertyId).then((response: any) => {
      if (!active) {
        return;
      }

      if (response.status === 200) {
        response.json().then(({ data }) => {
          if (!active || !data.length) return;
          setHomeOwnerDataVisibility(
            data.some((p) => p.name === 'Home Owner Data Visibility')
          );
        });
      }
    });

    return () => {
      active = false;
    };
  }, [serviceRequest.propertyId]);

  // Visibility logic.
  const shouldHideHomeOwner: boolean =
    isRole([USER_ROLES.SERVICE_PROVIDER, USER_ROLES.VENDOR], user) &&
    !homeOwnerDataVisibility;

  const shouldHideReporter: boolean =
    serviceRequest?.suite?.homeOwner?.id === serviceRequest?.reporter?.id &&
    shouldHideHomeOwner;

  const shouldShowHomeOwnerCard: boolean =
    serviceRequest.suite.homeOwner.id !== serviceRequest.reporter.id &&
    !shouldHideHomeOwner;

  const items: JSX.Element[] = [
    !shouldHideReporter && (
      <ServiceRequestReporterCard
        key="reporter"
        serviceRequest={serviceRequest}
        showLink={showLink}
      />
    ),
    shouldShowHomeOwnerCard && (
      <ServiceRequestHomeOwnerCard
        key="homeowner"
        serviceRequest={serviceRequest}
      />
    ),
    <ServiceRequestAssignedToCard
      key="assigned"
      serviceRequest={serviceRequest}
      showLink={showLink}
      navigateTo={navigateTo}
    />,
    <ServiceRequestAppointmentCard
      key="appointment"
      serviceRequest={serviceRequest}
    />,
  ].filter(Boolean);

  return (
    <HeadingWithDetails
      status={serviceRequest.status.name}
      breadcrumb={
        <ServiceRequestBreadcrumb
          user={user}
          location={location}
          branding={branding}
        />
      }
      toast={
        <ServiceRequestStatusToast
          serviceRequest={serviceRequest}
          handleStatusChange={handleStatusChange}
          user={user}
        />
      }
      headline={serviceRequest.ticketNumber ?? serviceRequest.ticketId}
      filedIn={
        <ServiceRequestLocationBreadcrumb
          serviceRequest={serviceRequest}
          user={user}
        />
      }
      items={items}
      actions={
        <ServiceRequestActions
          serviceRequest={serviceRequest}
          onEditServiceRequest={onEditServiceRequest}
          onDeleteServiceRequest={onDeleteServiceRequest}
          onDownloadPdf={onDownloadPdf}
          user={user}
        />
      }
      style={undefined}
    />
  );
};
